import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight } from 'lucide-react';
import { practiceAreas } from '../mock';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const PracticeAreas = () => {
  const categories = [...new Set(practiceAreas.map((a) => a.category))];

  const jumpTo = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="bg-cream text-ink">
      <Helmet>
        <title>Practice Areas & Sectors | Valentis Law Group</title>
        <meta name="description" content="Explore Valentis Law Group's full range of legal services, from commercial litigation, arbitration, and IPO consultation to sector-specific counsel for EV manufacturers, financial institutions, and IT firms." />
        <link rel="canonical" href="https://valentislawgroup.com/practice-areas" />
        <meta property="og:title" content="Practice Areas & Sectors | Valentis Law Group" />
        <meta property="og:description" content="Explore Valentis Law Group's full range of legal services, from commercial litigation, arbitration, and IPO consultation to sector-specific counsel for EV manufacturers, financial institutions, and IT firms." />
        <meta property="og:url" content="https://valentislawgroup.com/practice-areas" />
        <meta name="twitter:title" content="Practice Areas & Sectors | Valentis Law Group" />
        <meta name="twitter:description" content="Explore Valentis Law Group's full range of legal services, from commercial litigation, arbitration, and IPO consultation to sector-specific counsel for EV manufacturers, financial institutions, and IT firms." />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-40 pb-24 lg:pt-48 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-4xl">
            <p className="text-sm uppercase tracking-widest text-ink/60 mb-6">Our Expertise</p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-wide leading-tight mb-8">
              Legal Services Built Around Your Sector
            </h1>
            <p className="text-xl text-ink/70 leading-relaxed">
              The firm offers tailored solutions for individuals and businesses navigating intricate legal frameworks within their respective jurisdictions. Its services span recovery of outstanding dues, real estate, estate planning, corporate governance, regulatory compliance, intellectual property protection, dispute resolution and litigation, for clients across a wide range of industry sectors.
            </p>
          </div>
        </div>
      </section>

      {/* Index */}
      <section id="index" className="py-16 lg:py-20 border-t border-ink/10 bg-sand-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-sm uppercase tracking-[0.3em] text-ink/60 mb-3">Index</p>
          <h2 className="font-serif text-3xl md:text-4xl tracking-wide mb-10">Jump to a Practice Area</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-10">
            {categories.map((category) => (
              <div key={category}>
                <h3 className="text-xs uppercase tracking-[0.25em] text-ink/60 mb-4 pb-3 border-b border-ink/15">{category}</h3>
                <ol>
                  {practiceAreas.filter((a) => a.category === category).map((area) => (
                    <li key={area.id}>
                      <a
                        href={`#area-${area.id}`}
                        onClick={(e) => jumpTo(e, `area-${area.id}`)}
                        className="group flex items-baseline gap-4 py-2 text-ink/80 hover:text-ink transition-colors duration-300"
                      >
                        <span className="w-6 text-xs tracking-widest text-ink/40">{String(area.id).padStart(2, '0')}</span>
                        <span className="font-serif text-lg underline-offset-4 decoration-ink/40 group-hover:underline">{area.title}</span>
                      </a>
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Practice Areas Grid */}
      <section className="py-24 lg:py-32 border-t border-ink/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="space-y-16">
            {practiceAreas.map((area, index) => (
              <React.Fragment key={area.id}>
              {(index === 0 || practiceAreas[index - 1].category !== area.category) && (
                <div className={`pb-6 border-b border-ink/15 ${index === 0 ? '' : 'pt-16'}`}>
                  <p className="text-sm uppercase tracking-[0.3em] text-ink/60 mb-3">{area.category === 'Practice Areas' ? 'What we do' : 'Industries we advise'}</p>
                  <h2 className="font-serif text-4xl md:text-5xl tracking-wide">{area.category}</h2>
                </div>
              )}
              <div
                id={`area-${area.id}`}
                className={`scroll-mt-32 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
              >
                <div className={`space-y-6 ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="inline-block px-4 py-2 border border-ink/20 text-xs tracking-widest uppercase">
                      {String(index + 1).padStart(2, '0')} &middot; {area.tag}
                    </div>
                    <a
                      href="#index"
                      onClick={(e) => jumpTo(e, 'index')}
                      className="text-xs uppercase tracking-widest text-ink/50 hover:text-ink transition-colors duration-300"
                    >
                      &uarr; Back to index
                    </a>
                  </div>
                  <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-wide">
                    {area.title}
                  </h2>
                  <p className="text-xl text-ink/70 leading-relaxed">
                    {area.description}
                  </p>
                  {area.panel && (
                    <p className="inline-block bg-ink text-cream px-4 py-2 text-xs tracking-wider">
                      {area.panel}
                    </p>
                  )}
                  <p className="text-ink/60 leading-relaxed">
                    {area.details}
                  </p>
                  <Button
                    asChild
                    className="bg-ink text-cream hover:bg-ink/90 font-medium tracking-wider transition-all duration-300 hover:scale-105"
                  >
                    <Link to="/contact">
                      Discuss This Practice
                      <ArrowRight className="ml-2" size={18} />
                    </Link>
                  </Button>
                </div>

                <div className={`bg-ink/5 backdrop-blur-sm border border-ink/10 p-12 lg:p-16 hover:bg-ink/10 transition-all duration-500 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="space-y-6">
                    <h3 className="font-serif text-2xl mb-4">What We Handle</h3>
                    <ul className="space-y-3">
                      {(area.services || []).map((service, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-ink/40 mr-3">•</span>
                          <span className="text-ink/70">{service}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
              </React.Fragment>
            ))}
          </div>
          <p className="mt-16 border-l-4 border-ink/30 bg-sand-50 p-6 text-sm text-ink/70 leading-relaxed">
            <strong className="text-ink">Confidentiality Note:</strong> As a matter of professional ethics and contractual confidentiality, the Firm does not disclose the identities of clients or details of matters.
          </p>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-24 lg:py-32 bg-sand-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-wide mb-6">
              Supporting Services
            </h2>
            <p className="text-xl text-ink/70 max-w-3xl mx-auto">
              We provide comprehensive support for all aspects of your Dubai-India legal matters.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              'Legal Documentation',
              'Court Filings',
              'Case Status Updates',
              'Evidence Collection',
              'Negotiation Support',
              'Settlement Assistance',
              'Appeal Services',
              'Enforcement Proceedings',
              'Legal Consultation'
            ].map((service, index) => (
              <div key={index} className="bg-cream border border-ink/10 p-8 hover:border-ink/30 transition-all duration-300 group">
                <h3 className="font-serif text-xl mb-2 group-hover:text-ink/90 transition-colors">{service}</h3>
                <p className="text-sm text-ink/60">Expert assistance for your legal needs</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 border-t border-ink/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-serif text-4xl md:text-5xl tracking-wide mb-8">
            Ready to Resolve Your Matter?
          </h2>
          <p className="text-xl text-ink/70 mb-12">
            Contact us today to discuss your Dubai-India legal matter and learn how we can help.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-ink text-cream hover:bg-ink/90 px-8 py-6 text-base font-medium tracking-wider transition-all duration-300 hover:scale-105"
          >
            <Link to="/contact">Contact Our Team</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default PracticeAreas;